import { TFunction } from 'i18next';
import { isNullOrUndefined } from 'utils/util';

interface FormatPriceEntity {
  showCode: boolean;
  currency: string;
  formatted: string;
  formattedWithoutCode: string;
}

interface FormatPriceOptions {
  value: number | undefined;
  currency: string | undefined;
  t?: TFunction | undefined;
  l?: string | undefined;
}

export const currencies = {
  BRL: {
    codeISO: 'BRL',
    symbol: 'R$',
    decimals: 2,
    showCodeIsoAfter: false,
  },
  COP: {
    codeISO: 'COP',
    symbol: '$',
    decimals: 0,
    showCodeIsoAfter: true,
  },
  GTQ: {
    codeISO: 'GTQ',
    symbol: 'Q ',
    decimals: 2,
    showCodeIsoAfter: false,
  },
  USD: {
    codeISO: 'USD',
    symbol: '$',
    decimals: 2,
    showCodeIsoAfter: true,
  },
  EUR: {
    codeISO: 'EUR',
    symbol: '€',
    decimals: 2,
    showCodeIsoAfter: true,
  },
  MXN: {
    codeISO: 'MXN',
    symbol: '$',
    decimals: 0,
    showCodeIsoAfter: true,
  },
  ARS: {
    codeISO: 'ARS',
    symbol: '$',
    decimals: 0,
    showCodeIsoAfter: true,
  },
  CRC: {
    codeISO: 'CRC',
    symbol: '₡',
    decimals: 0,
    showCodeIsoAfter: false,
  },
  HNL: {
    codeISO: 'HNL',
    symbol: 'L',
    decimals: 0,
    showCodeIsoAfter: false,
  },
  SVC: {
    codeISO: 'SVC',
    symbol: '$',
    decimals: 0,
    showCodeIsoAfter: true,
  },
  NIO: {
    codeISO: 'NIO',
    symbol: 'C$',
    decimals: 0,
    showCodeIsoAfter: false,
  },
  DOP: {
    codeISO: 'DOP',
    symbol: '$',
    decimals: 0,
    showCodeIsoAfter: true,
  },
  GBP: {
    codeISO: 'GBP',
    symbol: '£',
    decimals: 2,
    showCodeIsoAfter: true,
  },
  CAD: {
    codeISO: 'CAD',
    symbol: 'C$',
    decimals: 2,
    showCodeIsoAfter: false,
  },
  AUD: {
    codeISO: 'AUD',
    symbol: 'AU$',
    decimals: 2,
    showCodeIsoAfter: false,
  },
  CLP: {
    codeISO: 'CLP',
    symbol: '$',
    decimals: 0,
    showCodeIsoAfter: true,
  },
  UYU: {
    codeISO: 'UYU',
    symbol: '$U',
    decimals: 0,
    showCodeIsoAfter: false,
  },
  PEN: {
    codeISO: 'PEN',
    symbol: 'S/.',
    decimals: 0,
    showCodeIsoAfter: false,
  },
  PYG: {
    codeISO: 'PYG',
    symbol: '₲',
    decimals: 0,
    showCodeIsoAfter: false,
  },
  BOB: {
    codeISO: 'BOB',
    symbol: 'Bs.',
    decimals: 2,
    showCodeIsoAfter: false,
  },
};

export function getFormattedPrice(options: FormatPriceOptions): FormatPriceEntity {
  if (!!options && !!Object.keys(options)?.length) {
    const locale = !!options.t ? options.t('locale') : !!options.l ? options.l : '';
    const showCode = showCurrencyCode({ code: options.currency });
    const { format: f } = formatConstructor({ code: options.currency, locale, showCode });
    const formatted = f(options.value || 0);
    let formattedWithoutCode = '';
    if (!!showCode) {
      const { format: fwc } = formatConstructor({ code: options.currency, locale, showCode: false });
      formattedWithoutCode = fwc(options.value || 0);
    } else {
      formattedWithoutCode = formatted;
    }
    return {
      showCode,
      currency: showCode && !isNullOrUndefined(options.currency) ? (options.currency as string) : '',
      formatted,
      formattedWithoutCode,
    };
  } else return { showCode: false, currency: '', formatted: '', formattedWithoutCode: '' };
}

function showCurrencyCode(options?: { code?: string; showCode?: boolean }): boolean {
  return (
    (!!options?.showCode || options?.showCode === undefined) &&
    !isNullOrUndefined(options?.code) &&
    getCurrencyConfiguration(options?.code || '').showCodeIsoAfter
  );
}

function formatConstructor(options?: { code?: string; locale: string; showSign?: boolean; showCode?: boolean }) {
  const currencyConfiguration = getCurrencyConfiguration(options?.code || '');

  const formatter = new Intl.NumberFormat(options?.locale, {
    minimumFractionDigits: currencyConfiguration.decimals,
    maximumFractionDigits: currencyConfiguration.decimals,
  });

  const showCode = showCurrencyCode({ code: options?.code, showCode: options?.showCode });

  return {
    format: (value: number) =>
      `${options?.showSign || options?.showSign === undefined ? currencyConfiguration.symbol : ''}${formatter.format(
        value,
      )}${showCode ? ` ${options?.code}` : ''}`,
  };
}

export function getCurrencyConfiguration(currencyCode: string) {
  if (currencyCode === undefined || currencyCode === '') {
    return currencies.COP;
  }

  return currencies[currencyCode.toUpperCase()] || currencies.COP;
}
